import React, { useState, useCallback, useEffect, useRef } from "react";
import { GoogleMap, InfoWindow } from "@react-google-maps/api";
import { Skeleton } from "@mui/material";
import { FaStar } from "react-icons/fa";
import { RiShip2Fill } from "react-icons/ri";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShip } from '@fortawesome/free-solid-svg-icons';

const containerStyle = {
  width: "100%",
  height: "100%",
};

const defaultCenter = {
  lat: 39.8283,
  lng: -98.5795,
};

const GoogleMaps = ({ boatLocations }) => {
  const [map, setMap] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  // Inject CSS to strip Google Maps InfoWindow outer padding
  useEffect(() => {
    const styleId = "gm-infowindow-override";
    if (document.getElementById(styleId)) document.getElementById(styleId).remove();
    const style = document.createElement("style");
    style.id = styleId;
    style.innerHTML = `
      /* Remove all padding and background from InfoWindow container */
      .gm-style .gm-style-iw-c {
        padding: 0 !important;
        border-radius: 12px !important;
        box-shadow: 0 6px 24px rgba(0,0,0,0.28) !important;
        min-width: 0 !important;
        max-width: 90vw !important;
      }
      /* Remove scroll container padding */
      .gm-style .gm-style-iw-d {
        overflow: hidden !important;
        padding: 0 !important;
        max-height: none !important;
      }
      /* Remove the invisible header bar that pushes content down */
      .gm-style .gm-style-iw-chr {
        height: 0px !important;
        padding: 0 !important;
      }
      /* Hide the InfoWindow tail arrow */
      .gm-style-iw-t::after { display: none !important; }
      /* X close button — place firmly on the image top-right */
      .gm-style button.gm-ui-hover-effect {
        top: 4px !important;
        right: 4px !important;
        width: 24px !important;
        height: 24px !important;
        z-index: 200 !important;
        background: rgba(0,0,0,0.55) !important;
        border-radius: 50% !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        opacity: 1 !important;
      }
      /* White X icon */
      .gm-style button.gm-ui-hover-effect > span {
        width: 14px !important;
        height: 14px !important;
        margin: 0 !important;
        filter: invert(1) !important;
      }
    `;
    document.head.appendChild(style);
  }, []);


  const onLoad = useCallback(function callback(map) {
    mapRef.current = map;
    setMap(map);
  }, []);

  const onUnmount = useCallback(function callback() {
    // Clean up markers
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    setMap(null);
    mapRef.current = null;
  }, []);

  const onClose = () => {
    setSelectedPlace(null);
  };

  // Imperatively create markers when map or boatLocations change
  // This bypasses @react-google-maps/api's <Marker> component timing issues
  useEffect(() => {
    if (!map || !window.google) return;

    // Clear previous markers
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    if (!boatLocations || boatLocations.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    let hasValidLocation = false;

    // Build icon safely
    let icon = undefined;
    try {
      if (window.google.maps.Size) {
        icon = {
          url: `${process.env.REACT_APP_BASE_URL}/images/bassboat.png`,
          scaledSize: new window.google.maps.Size(40, 40),
        };
      }
    } catch (e) {
      // Fall back to default pin
      icon = undefined;
    }

    boatLocations.forEach((location) => {
      const lat = parseFloat(location.lat);
      const lng = parseFloat(location.lng);

      if (isNaN(lat) || isNaN(lng)) return;

      const marker = new window.google.maps.Marker({
        position: { lat, lng },
        map: map,
        title: location.name,
        icon: icon,
      });

      marker.addListener("click", () => {
        setSelectedPlace(location);
      });

      markersRef.current.push(marker);
      bounds.extend({ lat, lng });
      hasValidLocation = true;
    });

    if (hasValidLocation) {
      map.fitBounds(bounds);

      // Adjust zoom after fitting bounds
      window.google.maps.event.addListenerOnce(map, "idle", () => {
        const zoom = map.getZoom();
        if (boatLocations.length === 1) {
          if (zoom < 10) map.setZoom(10);
        } else if (zoom > 14) {
          map.setZoom(14);
        }
      });
    }

    // Cleanup function
    return () => {
      markersRef.current.forEach(m => m.setMap(null));
      markersRef.current = [];
    };
  }, [map, boatLocations]);

  if (!window.google) {
    return (
      <div style={{ padding: "0", height: "100%", width: "100%" }}>
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          sx={{ bgcolor: "grey.300", borderRadius: "10px" }}
        />
      </div>
    );
  }

  return (
    <div className="width-100" style={{ height: "100%", width: "100%" }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={defaultCenter}
        zoom={4}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          mapTypeControl: false,
          zoomControl: true,
          fullscreenControl: true,
          streetViewControl: true,
        }}
      >
        {selectedPlace && (
          <InfoWindow
            position={{
              lat: parseFloat(selectedPlace.lat),
              lng: parseFloat(selectedPlace.lng)
            }}
            onCloseClick={onClose}
          >
            {selectedPlace.listing ? (
              /* margin bleeds our card to the InfoWindow frame edges so the X button sits on the image */
              <div style={{ minWidth: "200px", maxWidth: "min(260px, 90vw)", overflow: "hidden", fontFamily: "sans-serif" }}>
                <a href={selectedPlace.link} style={{ textDecoration: "none", color: "inherit", display: "block" }}>

                  {/* Full-bleed bg image — X button from Google Maps floats on top-right */}
                  <div style={{
                    position: "relative", height: "140px",
                    backgroundImage: `url(${selectedPlace.listing.featured_image})`,
                    backgroundSize: "cover", backgroundPosition: "center"
                  }}>
                    {/* Gradient: dark top (for X button contrast) + dark bottom (for title readability) */}
                    <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.05) 45%, rgba(0,0,0,0.65) 100%)" }}></div>

                    {/* Owner badge — top left */}
                    <div style={{
                      position: "absolute", top: "16px", left: "8px", zIndex: 10,
                      display: "flex", alignItems: "center", gap: "4px", height: "19px",
                      backgroundColor: "rgba(0,0,0,0.5)", padding: "3px 8px",
                      borderRadius: "20px", maxWidth: "70%"
                    }}>
                      {selectedPlace.ownerDetails?.avatar && (
                        <img src={selectedPlace.ownerDetails.avatar} alt="owner"
                          style={{ width: "16px", height: "16px", borderRadius: "50%", objectFit: "cover" }} />
                      )}
                      <span style={{ color: "white", fontSize: "10px", fontWeight: "600", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {selectedPlace.ownerDetails ? `${selectedPlace.ownerDetails.firstName} ${selectedPlace.ownerDetails.lastName}` : ""}
                      </span>
                      <div style={{ display: "flex", gap: "1px" }}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <FaStar key={star} size={7} style={{ color: star <= selectedPlace.rating ? "#FFD700" : "#bbb" }} />
                        ))}
                      </div>
                    </div>

                    {/* Title + location — bottom of image */}
                    <div style={{ position: "absolute", bottom: "8px", left: "8px", right: "8px", zIndex: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                        <h4 style={{
                          fontSize: "12px", fontWeight: "700", color: "white", margin: 0,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          flex: 1, textShadow: "0 1px 4px rgba(0,0,0,0.8)"
                        }}>
                          {selectedPlace.listing.listingTitle}
                        </h4>
                        {selectedPlace.listing.fishingGuideDetails?.calendarRules?.instantBook && (
                          <span style={{ fontSize: "8px", backgroundColor: "#ddb467", color: "#000", padding: "1px 5px", borderRadius: "8px", flexShrink: 0, fontWeight: "700" }}>
                            ⚡ Instant
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: "10px", color: "rgba(255,255,255,0.82)", margin: "2px 0 0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {selectedPlace.locationName || ""}
                      </p>
                    </div>

                    {/* Boat length — bottom right */}
                    {selectedPlace.listing.boatLength && (
                      <div style={{
                        position: "absolute", bottom: "8px", right: "8px", zIndex: 10,
                        backgroundColor: "rgba(0,0,0,0.55)", padding: "2px 6px",
                        borderRadius: "10px", display: "flex", alignItems: "center",
                        gap: "3px", color: "white", fontSize: "10px"
                      }}>
                        <RiShip2Fill />
                        <span>{selectedPlace.listing.boatLength} ft</span>
                      </div>
                    )}
                  </div>

                  {/* White bottom strip */}
                  <div style={{ padding: "8px 10px 10px", backgroundColor: "white" }}>
                    <div style={{ color: "#555", fontSize: "11px", marginBottom: "6px" }}>
                      {selectedPlace.listing.boatType && (
                        <>
                          <FontAwesomeIcon icon={faShip} style={{ marginRight: "4px", color: "#ff6b35" }} />
                          {selectedPlace.listing.boatType}
                        </>
                      )}
                      {selectedPlace.listing.capacity && (
                        <> • {selectedPlace.listing.capacity} pax</>
                      )}
                    </div>
                    {selectedPlace.listing.pricing && (
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <span style={{ fontSize: "9px", color: "#999", display: "block" }}>Trip starts from</span>
                          <span style={{ fontSize: "15px", fontWeight: "700", color: "#222" }}>
                            US ${selectedPlace.listing.pricing.tripsStartingFrom || selectedPlace.listing.pricing.hourlyRate || selectedPlace.listing.pricing.hourlyOptions?.[0]?.price || "N/A"}
                          </span>
                        </div>
                        <button style={{
                          backgroundColor: "#ddb467", border: "none", color: "#000",
                          padding: "6px 12px", borderRadius: "5px", fontSize: "11px",
                          fontWeight: "700", cursor: "pointer"
                        }}>
                          View →
                        </button>
                      </div>
                    )}
                  </div>
                </a>
              </div>
            ) : (
              <div>
                <h5>{selectedPlace.name}</h5>
                <a href={selectedPlace.link}>View Boat</a>
              </div>
            )}
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
};

export default GoogleMaps;
